import { useState } from "react";
import { Link, useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, ChevronRight, Phone } from "lucide-react";
import { useQuoteDrawer } from '../contexts/QuoteDrawerContext';
import { Button } from "@/components/ui/button";
import "./Header.css";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [location] = useLocation();
  const { openDrawer } = useQuoteDrawer();

  const services = [
    { name: "Software Development", href: "/software-development" },
    { name: "Web Design & Development", href: "/web-design-development" },
    { name: "Mobile App Development", href: "/mobile-app-development" },
    { name: "Data Analytics", href: "/data-analytics" },
    { name: "Digital Marketing", href: "/digital-marketing" },
    { name: "eCommerce Solutions", href: "/ecommerce" },
    { name: "Consulting & Strategy", href: "/consulting" },
    { name: "Branding Services", href: "/branding" },
    { name: "UI/UX Design", href: "/ui-ux" },
    { name: "SEO Services", href: "/seo" },
    { name: "Email Hosting", href: "/email-hosting" },
    { name: "Web Hosting", href: "/web-hosting" },
    { name: "Domain Registration", href: "/domain-registration" },
    { name: "ERP / CRM Systems", href: "/erp-crm" },
    { name: "Cloud & DevOps", href: "/cloud-services" },
    { name: "Cyber Security", href: "/cyber-security" },
    { name: "Maintenance & Support", href: "/maintenance-support" },
    { name: "Custom App Development", href: "/custom-apps" },
  ];

  const mobileMenuVariants = {
    open: { 
      x: 0,
      transition: { 
        type: 'spring', 
        stiffness: 300, 
        damping: 40 
      } 
    },
    closed: { 
      x: '100%',
      transition: { 
        type: 'spring', 
        stiffness: 300, 
        damping: 40,
        delay: 0.15
      } 
    }
  };

  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 100, damping: 20 }}
      className="fixed top-0 w-full z-50 bg-background/90 backdrop-blur-md border-b border-border/20 py-1"
    >
      <nav className="container mx-auto px-4 py-1">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center space-x-2">
            <img 
              src="/images/firstsoftlogo.webp" 
              alt="First Soft UAE Logo" 
              className="w-12 h-12 rounded-lg shadow-[0_4px_8px_rgba(124,58,237,0.3),0_8px_16px_rgba(124,58,237,0.2)]"
            />
            <div className="flex flex-col justify-center items-center">
              <span className="font-bold text-xl leading-tight tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-purple-700 via-pink-600 to-purple-700 bg-[length:200%] px-1.5 py-0.5 rounded-md bg-gray-900/80 backdrop-blur-sm border border-purple-300/50 shadow-lg">
                First <span className="text-black font-bold">Soft UAE</span>
              </span>
              <span className="text-[0.65rem] font-medium uppercase tracking-[0.15em] text-purple-600/70 mt-0.5 bg-purple-100/50 px-1.5 py-0.5 rounded-full">
                 Technologies
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            <NavigationMenu className="bg-background border border-border/20 rounded-lg px-1 shadow-sm">
              <NavigationMenuList className="flex space-x-1">
                <NavigationMenuItem>
                  <Link 
                    href="/" 
                    className={`px-4 py-2 rounded-lg transition-colors duration-200 text-sm font-medium ${location === '/' 
                      ? 'text-purple-600 font-semibold' 
                      : 'text-foreground/90 hover:text-purple-500'}`}
                  >
                    Home
                  </Link>
                </NavigationMenuItem>
                
                <NavigationMenuItem>
                  <NavigationMenuTrigger 
                    className=" text-sm font-medium text-foreground/90 hover:text-black-500 data-[state=open]:text-black "
                  >
                    Our Services
                  </NavigationMenuTrigger>
                  
                  <NavigationMenuContent 
                    className="min-w-[280px] rounded-lg bg-background/95 backdrop-blur-lg border border-purple-200/30 shadow-lg p-1"
                  >
                    <div 
                      className="relative grid grid-cols-2 gap-2 p-2 w-[500px] bg-background/90"
                    >
                      <div 
                        className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-purple-600/10 via-transparent to-transparent opacity-70"
                      ></div>
                      {services.map((service) => (
                        <Link
                          key={service.href}
                          href={service.href}
                          className="group relative z-10 p-2 rounded-md border border-transparent hover:border-purple-200 hover:bg-purple-50/70 transition-all duration-200 shadow-sm hover:shadow-purple-200/50"
                        >
                          <div className="font-medium text-sm text-foreground group-hover:text-purple-600 transition-colors flex items-center">
                            <span className="w-2 h-2 rounded-full bg-purple-500 mr-2 group-hover:bg-purple-600 transition-colors"></span>
                            {service.name}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <Link 
                    href="/about" 
                    className={`px-4 py-2 rounded-lg transition-colors duration-200 text-sm font-medium ${location === '/about' 
                      ? 'text-purple-600 font-semibold' 
                      : 'text-foreground/90 hover:text-purple-500'}`}
                  >
                    About Us
                  </Link>
                </NavigationMenuItem>
                
                <NavigationMenuItem>
                  <Link 
                    href="/blog" 
                    className={`px-4 py-2 rounded-lg transition-colors duration-200 text-sm font-medium ${location === '/blog' 
                      ? 'text-purple-600 font-semibold' 
                      : 'text-foreground/90 hover:text-purple-500'}`}
                  >
                    Blog
                  </Link>
                </NavigationMenuItem>
                
                <NavigationMenuItem>
                  <Link 
                    href="/contact" 
                    className={`px-4 py-2 rounded-lg transition-colors duration-200 text-sm font-medium ${location === '/contact' 
                      ? 'text-purple-600 font-semibold' 
                      : 'text-foreground/90 hover:text-purple-500'}`}
                  >
                    Contact Us
                  </Link>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
            
            
          </div>

          {/* Right section with contact info and CTA */}
          
          <div className="flex items-center space-x-4">
            <div className="hidden md:flex items-center space-x-6">
            <a 
              href="https://wa.me/447743307781" 
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:flex text-foreground/80 hover:text-green-500 transition-colors"
              aria-label="Chat on WhatsApp"
              role="link"
              title="Chat with us on WhatsApp"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.298-.018-.46.13-.608.134-.133.298-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.226 1.36.194 1.87.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.652a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
            </a>
              <a
                href="tel:+971582481443" 
                className="font-medium text-sm bg-purple-100/80 hover:bg-purple-200/80 px-3 py-1.5 rounded-full border border-purple-200 text-purple-800 transition-colors duration-200 flex items-center gap-1.5"
              >
                <Phone className="w-4 h-4" />
                +971-58-248-1443
              </a>
            </div>
            
            
            <Button 
              onClick={(e) => { 
                e.preventDefault();
                openDrawer(); 
              }} 
              className="ml-2 bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white shadow-md hover:shadow-purple-500/30 transition-all duration-300"
            >
              Get Free Quote
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="lg:hidden text-foreground/80 hover:bg-transparent hover:text-purple-500"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle navigation menu"
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
            >
              {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>

          {/* Mobile Menu */}
          <AnimatePresence>
            {isMobileMenuOpen && (
              <motion.div
                initial={mobileMenuVariants.closed}
                animate={mobileMenuVariants.open}
                exit={mobileMenuVariants.closed}
                className="lg:hidden fixed inset-x-0 top-16 bg-background border-b border-border/20 shadow-lg"
              >
                <div className="relative w-full">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="absolute top-2 right-4 text-foreground/80 hover:bg-transparent hover:text-purple-500"
                    onClick={() => setIsMobileMenuOpen(false)}
                    aria-label="Close navigation menu"
                    aria-expanded={isMobileMenuOpen}
                    aria-controls="mobile-menu"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                  
                  <div className="px-4 pt-12 pb-4 space-y-1 max-h-[calc(100vh-4rem)] overflow-y-auto">
                    <div className="space-y-1">
                      <Link 
                        href="/" 
                        className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${location === '/' 
                          ? 'text-purple-600 bg-purple-50/70' 
                          : 'text-foreground hover:bg-purple-50/50 hover:text-purple-600'}`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <span className="w-2 h-2 rounded-full bg-purple-500 mr-2"></span>
                        Home
                      </Link>
                      
                      {/* Services Dropdown */}
                      <div className="space-y-1">
                        <button 
                          onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                          className={`flex items-center justify-between w-full px-3 py-2 rounded-md text-sm font-medium ${mobileServicesOpen 
                            ? 'text-purple-600 bg-purple-50/70' 
                            : 'text-foreground hover:bg-purple-50/50 hover:text-purple-600'}`}
                          aria-label="Toggle services menu"
                          aria-expanded={mobileServicesOpen}
                          aria-controls="services-menu"
                        >
                          <div className="flex items-center">
                            <span className="w-2 h-2 rounded-full bg-purple-500 mr-2"></span>
                            <span>Our Services</span>
                          </div>
                          <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''}`} />
                        </button>
                        
                        <div className={`pl-4 space-y-1 overflow-hidden transition-all duration-200 ${mobileServicesOpen ? 'max-h-[1000px]' : 'max-h-0'}`}>
                          {services.map((service) => (
                            <Link
                              key={service.href}
                              href={service.href}
                              className="flex items-center px-3 py-2 text-sm rounded-md hover:bg-purple-50/50 hover:text-purple-600 transition-colors"
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              <span className="w-2 h-2 rounded-full bg-purple-400 mr-2"></span>
                              <span className="flex-1">{service.name}</span>
                            </Link>
                          ))}
                        </div>
                      </div>
                      
                      <Link 
                        href="/about" 
                        className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${location === '/about' 
                          ? 'text-purple-600 bg-purple-50/70' 
                          : 'text-foreground hover:bg-purple-50/50 hover:text-purple-600'}`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <span className="w-2 h-2 rounded-full bg-purple-500 mr-2"></span>
                        About Us
                      </Link>
                      
                      <Link 
                        href="/blog" 
                        className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${location === '/blog' 
                          ? 'text-purple-600 bg-purple-50/70' 
                          : 'text-foreground hover:bg-purple-50/50 hover:text-purple-600'}`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <span className="w-2 h-2 rounded-full bg-purple-500 mr-2"></span>
                        Blog
                      </Link>
                      
                      <Link 
                        href="/contact" 
                        className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${location === '/contact' 
                          ? 'text-purple-600 bg-purple-50/70' 
                          : 'text-foreground hover:bg-purple-50/50 hover:text-purple-600'}`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <span className="w-2 h-2 rounded-full bg-purple-500 mr-2"></span>
                        Contact Us
                      </Link>
                    </div>
                    
                    <div className="pt-1">
                      <Button 
                        onClick={(e) => { 
                          e.preventDefault();
                          openDrawer(); 
                        }} 
                        className="w-full bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white shadow-md hover:shadow-purple-500/30"
                      >
                        Get Free Quote
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

      </nav>
    </motion.header>
  );
}
